/**
 * useWorkflowStatusesList Hook
 * Fetches workflow statuses for dropdown use
 */

import { useState, useEffect } from 'react';
import { graphqlClient } from '@/services/core/graphqlClient';
import { GET_ALL_WORKFLOW_STATUSES_FOR_DROPDOWN } from '@/services/resources/workflowGquery';
import { useAuth } from '@/contexts/AuthContext';

export interface WorkflowStatusOption {
  workflow_status_id: number;
  workflow_status: string;
}

export interface UseWorkflowStatusesListResult {
  workflowStatuses: WorkflowStatusOption[];
  loading: boolean;
  error: string | null;
}

export const useWorkflowStatusesList = (): UseWorkflowStatusesListResult => {
  const { hasuraToken } = useAuth();
  const [workflowStatuses, setWorkflowStatuses] = useState<WorkflowStatusOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchWorkflowStatuses = async () => {
      if (!hasuraToken) {
        setError('Authentication required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const response = await graphqlClient.request(
          {
            query: GET_ALL_WORKFLOW_STATUSES_FOR_DROPDOWN,
            variables: {}
          },
          hasuraToken
        );

        if (response.success && response.data?.pep_workflow_status) {
          setWorkflowStatuses(response.data.pep_workflow_status);
        } else {
          const errorMessage = response.errors?.join(', ') || 'Failed to fetch workflow statuses';
          setError(errorMessage);
        }
      } catch (err: any) {
        setError(err.message || 'Failed to fetch workflow statuses');
        console.error('Error fetching workflow statuses:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchWorkflowStatuses();
  }, [hasuraToken]);

  return {
    workflowStatuses,
    loading,
    error
  };
};
